
import React from 'react';

// Inform TypeScript that 'marked' is a global variable from a script tag.
declare const marked: {
  parse(markdownString: string): string;
};

interface GeminiAdvisorProps {
  onRun: () => void;
  isRunning: boolean;
  content: string;
  hasArticles: boolean;
  isApiKeyMissing: boolean;
}

const MissingApiKeyWarning: React.FC = () => (
    <div className="flex items-center justify-center h-full text-center p-4 bg-red-50 text-red-700 rounded-lg border border-red-200">
        <div>
            <i className="fas fa-exclamation-triangle fa-2x mb-3 text-red-400"></i>
            <h4 className="font-bold text-lg">Configurazione Richiesta</h4>
            <p className="mt-1">La chiave API di Google non è stata trovata.</p>
            <p className="text-sm mt-2">
                Per attivare il Consulente AI, per favore imposta la variabile d'ambiente 
                <code className="bg-red-200 text-red-900 font-mono p-1 rounded-md text-xs mx-1">API_KEY</code>.
            </p>
        </div>
    </div>
);

const GeminiAdvisor: React.FC<GeminiAdvisorProps> = ({ onRun, isRunning, content, hasArticles, isApiKeyMissing }) => {
  const getPanelContent = () => {
    if (isApiKeyMissing) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-4">
            <MissingApiKeyWarning />
        </div>
      );
    }
    if (isRunning && !content) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-gray-500 p-4">
          <div className="w-8 h-8 border-2 border-dashed rounded-full animate-spin border-sky-500"></div>
          <p className="mt-3 text-sm">Contattando Gemini, un momento...</p>
        </div>
      );
    }
    if (content) {
        // Safely parse the markdown content. Use marked if available, otherwise use a simple fallback.
        const htmlContent = typeof marked !== 'undefined' 
            ? marked.parse(content)
            : content.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br />');

        return <div className="prose prose-sm max-w-none h-full overflow-y-auto p-4" dangerouslySetInnerHTML={{ __html: htmlContent }} />;
    }
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-500 text-center p-4">
        <div>
          <i className="fas fa-lightbulb fa-3x text-gray-300 mb-4"></i>
          <p className="font-semibold text-lg">Pronto per l'analisi</p>
          <p className="text-gray-500">I suggerimenti dell'AI appariranno qui.</p>
           {!hasArticles && <p className="text-sm text-orange-600 mt-2 font-medium">Aggiungi prima degli articoli alla collezione per abilitare questa funzione.</p>}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-lg flex flex-col h-full">
      <div className="flex justify-between items-center border-b pb-2">
        <h3 className="text-lg font-bold text-gray-700">
            <i className="fas fa-magic-wand-sparkles text-sky-500 mr-2"></i>
            Consulente AI per Collezioni
        </h3>
        <button
          onClick={onRun}
          disabled={isRunning || !hasArticles || isApiKeyMissing}
          className="bg-gradient-to-r from-sky-500 to-indigo-600 text-white font-semibold px-4 py-2 rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-md w-48 text-center"
          title={isApiKeyMissing ? "La chiave API non è configurata" : !hasArticles ? "Aggiungi articoli prima di chiedere una consulenza" : "Ottieni suggerimenti dall'AI"}
        >
          {isRunning ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2"></i>
              Elaborazione...
            </>
          ) : 'Ottieni Suggerimenti'}
        </button>
      </div>
      <div className="flex-grow bg-gray-50 rounded-lg mt-4 overflow-hidden">
        <div className="h-full w-full">
            {getPanelContent()}
        </div>
      </div>
    </div>
  );
};

export default GeminiAdvisor;
